import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAdsOzet, type AdsRange } from "@/api/ads";
import { useKpiSnapshot, type KpiEntry } from "@/api/kpi";
import { useOzet } from "@/api/ozet";
import { useSiteMetrikleri } from "@/api/siteMetrikleri";
import { AdsBlock } from "@/components/AdsBlock";
import { AlarmManager } from "@/components/AlarmManager";
import { DeltaModeToggle } from "@/components/DeltaModeToggle";
import { FunnelSvg } from "@/components/FunnelSvg";
import { SectionHeader } from "@/components/ui";
import { alarmFromKpis } from "@/features/kpi/alarm";
import { readStoredAdsRange } from "@/features/kpi/AdsAralik";
import { hasFunnelData } from "@/features/kpi/funnel";
import { HarcamaEtkisi } from "@/features/kpi/HarcamaEtkisi";
import { OrtakHavuz } from "@/features/kpi/OrtakHavuz";
import { applyPeriodFilter } from "@/features/kpi/periodFilter";
import { ReklamHunisi } from "@/features/kpi/ReklamHunisi";
import { SiteMetrikleri } from "@/features/kpi/SiteMetrikleri";
import { AiAnalyzeModal } from "@/features/ozet/AiAnalyzeModal";
import { KpiDetailModal } from "@/features/ozet/KpiDetailModal";
import { useDeltaMode } from "@/hooks/useDeltaMode";
import { useKpiPeriod } from "@/hooks/useKpiPeriod";
import { usePeriod } from "@/hooks/usePeriod";
import { t } from "@/i18n";
import { fmtInt, fmtMoney } from "@/lib/utils";

/**
 * /kpi — sıra: (1) Reklam performansı [Ortak Havuz → huni → harcama etkisi →
 * Meta | Google] → (2) E-ticaret satış hunisi (GA4, veri yoksa gizli) → (3)
 * Site metrikleri. Tasarım (17.09.2026): hiçbir reklam hesabı bağlı değilse
 * reklam bölümü dört boş kart yerine TEK kompakt karta iner; sağlık halkası
 * yalnız Özet'te (burada tekrar edilmez).
 */
export function Kpi() {
  const { period, dateRange } = usePeriod();
  const { kpiPeriod } = useKpiPeriod();
  const { deltaMode, setDeltaMode } = useDeltaMode();

  const snapshotQuery = useKpiSnapshot();
  const ozetQuery = useOzet(period, dateRange);
  const siteMetrikleriQuery = useSiteMetrikleri(kpiPeriod);

  const [adsRange, setAdsRange] = useState<AdsRange>(() => readStoredAdsRange() ?? { gun: period });
  const adsQuery = useAdsOzet(adsRange, true);
  const kaynaklar = adsQuery.data?.kaynaklar ?? [];
  const metaKaynak = kaynaklar.find((k) => k.network === "meta");
  const googleKaynak = kaynaklar.find((k) => k.network === "google");
  const hasRecords = (source: typeof metaKaynak) => source != null && (source.status === "recorded" || source.spend > 0 || source.clicks > 0 || source.impressions > 0);
  const metaLive = hasRecords(metaKaynak);
  const googleLive = hasRecords(googleKaynak);

  const [detailKpi, setDetailKpi] = useState<{ id: string; title: string } | null>(null);
  const [analyzeTarget, setAnalyzeTarget] = useState<{ id: string; title: string; rows?: KpiEntry[] } | null>(null);

  const rawKpis = snapshotQuery.data?.kpis ?? null;
  const filtered = useMemo(() => (rawKpis ? applyPeriodFilter(rawKpis, kpiPeriod) : null), [rawKpis, kpiPeriod]);
  const alarm = useMemo(() => alarmFromKpis(filtered?.kpis ?? rawKpis), [filtered, rawKpis]);

  const pazarlama = ozetQuery.data?.pazarlama;
  // The overview's top-level marketing values combine networks and may use
  // a different date range. Each network card uses this ads query exclusively.
  const metaStatus = pazarlama?.meta?.status === "error" ? "error" : metaLive ? "live" : (pazarlama?.meta?.status === "live" ? "no_data" : pazarlama?.meta?.status ?? "not_connected");
  const googleStatus = googleLive ? "live" : (pazarlama?.google?.status === "live" ? "no_data" : pazarlama?.google?.status ?? "not_supported");
  const reklamVerisiVar = (adsQuery.data?.gunluk ?? []).some((g) => g.toplam_spend > 0) || metaStatus === "live" || googleLive;
  const reklamBagliDegil = !ozetQuery.isLoading && !adsQuery.isLoading && !reklamVerisiVar && googleStatus !== "no_data" && metaStatus !== "no_data";

  function openDetail(kpiId: string, title: string) {
    setDetailKpi({ id: kpiId, title });
  }

  return (
    <section className="space-y-6">
      <AlarmManager level={alarm.level} criticalCount={alarm.criticalCount} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-navy">{t("nav.kpi")}</h1>
          <p className="text-[13px] text-muted">{t("kpi.title_sub")}</p>
        </div>
        <DeltaModeToggle mode={deltaMode} onChange={setDeltaMode} />
      </div>

      {snapshotQuery.isError && (
        <p role="alert" className="ribbon ribbon-warn text-sm">
          {t("kpi.snapshot_hata")}
        </p>
      )}

      {/* (1) Reklam performansı */}
      <div className="space-y-4" data-testid="kpi-reklam-bolumu">
        <SectionHeader title={t("kpi.sec.reklam")} note={t("kpi.sec.reklam_not")} />
        {reklamBagliDegil ? (
          <div className="card flex flex-wrap items-center justify-between gap-3" data-testid="kpi-reklam-bagli-degil">
            <p className="text-sm text-muted">
              {metaStatus === "error" ? t("kpi.reklam.meta_veri_alinamadi") : t("kpi.reklam.bagli_degil")}
            </p>
            <Link to="/ayarlar" className="btn btn-outline btn-sm">
              {t("ozet.pazarlama.baglanti_link")} →
            </Link>
          </div>
        ) : (
          <>
            <OrtakHavuz data={adsQuery.data} isLoading={adsQuery.isLoading} isError={adsQuery.isError} range={adsRange} onRangeChange={setAdsRange} />
            <ReklamHunisi data={adsQuery.data} isLoading={adsQuery.isLoading} isError={adsQuery.isError} />
            <HarcamaEtkisi
              cur={adsQuery.data?.toplam}
              onceki={adsQuery.data?.onceki}
              deltaMode={deltaMode}
              gunlukSayisi={adsQuery.data?.gunluk.filter((g) => g.toplam_spend > 0).length ?? 0}
            />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <AdsBlock
                title={t("kpi.meta_ads")}
                status={metaStatus}
                reason={metaStatus === "not_connected" ? "baglanti_eksik" : null}
                errorMessage={metaStatus === "error" ? t("kpi.reklam.meta_veri_alinamadi") : null}
                setupHref="/ayarlar"
                metrics={
                  metaStatus === "live" && metaKaynak
                    ? [
                        { label: t("ozet.metrik.reklam_harcama"), value: fmtMoney(metaKaynak.spend) },
                        { label: t("kpi.reklam_satis_degeri"), value: fmtMoney(metaKaynak.conversion_value) },
                        { label: t("ozet.metrik.roas"), value: fmtRoas(metaKaynak.roas) },
                        { label: t("kpi.tiklama"), value: fmtInt(metaKaynak.clicks) },
                      ]
                    : []
                }
              />
              <AdsBlock
                title={t("kpi.google_ads")}
                status={googleStatus}
                setupHref="/ayarlar"
                metrics={
                  googleLive && googleKaynak
                    ? [
                        { label: t("ozet.metrik.reklam_harcama"), value: fmtMoney(googleKaynak.spend) },
                        { label: t("kpi.reklam_satis_degeri"), value: fmtMoney(googleKaynak.conversion_value) },
                        { label: t("ozet.metrik.roas"), value: fmtRoas(googleKaynak.roas) },
                        { label: t("kpi.tiklama"), value: fmtInt(googleKaynak.clicks) },
                      ]
                    : []
                }
              />
            </div>
          </>
        )}
      </div>

      {/* (2) E-Ticaret satış hunisi — GA4, veri yoksa tamamen gizli */}
      {hasFunnelData(ozetQuery.data?.trafik.huni) && (
        <div data-testid="kpi-huni">
          <SectionHeader title={t("kpi.huni")} note={t("kpi.huni.alt")} />
          <div className="card mt-3">
            <FunnelSvg stages={ozetQuery.data?.trafik.huni ?? []} />
          </div>
        </div>
      )}

      {/* (3) Site metrikleri */}
      <SiteMetrikleri
        data={siteMetrikleriQuery.data}
        isLoading={siteMetrikleriQuery.isLoading}
        isError={siteMetrikleriQuery.isError}
        deltaMode={deltaMode}
        onOpenDetail={openDetail}
      />

      <KpiDetailModal
        open={detailKpi != null}
        kpiId={detailKpi?.id ?? null}
        title={detailKpi?.title ?? ""}
        onClose={() => setDetailKpi(null)}
        onAnalyze={(rows) => {
          if (detailKpi) setAnalyzeTarget({ id: detailKpi.id, title: detailKpi.title, rows });
        }}
      />

      <AiAnalyzeModal
        open={analyzeTarget != null}
        title={analyzeTarget?.title ?? ""}
        kpiId={analyzeTarget?.id ?? null}
        days={period}
        modalRows={analyzeTarget?.rows}
        onClose={() => setAnalyzeTarget(null)}
      />
    </section>
  );
}

function fmtRoas(v: number | null | undefined): string {
  if (v == null) return "—";
  return v.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + "x";
}
